/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

export interface ITTLCacheOptions {
    removalThresholdInMs?: number;
    intervalForRemovalInMs?: number;
}

export type TOnRemoveCallback = (removedKey: string) => void;
/**
 * Cache with TTL and polling to remove items from it, default implementaiton is to only poll on server
 */
export class TTLCache<TValue> {
    // threshold for removal, 5mins
    public readonly removalThresholdInMs: number;

    // frequency of cleaning, 2mins -- this is the period it'll look for keys older than removalThresholdInMs
    public readonly intervalForRemovalInMs: number;

    // internal cache
    private _cache: Map<string, [TValue, number, TOnRemoveCallback?]>;

    constructor(options?: ITTLCacheOptions) {
        this.removalThresholdInMs = (options && options.removalThresholdInMs) || 300000;
        this.intervalForRemovalInMs = (options && options.intervalForRemovalInMs) || 120000;
        this._cache = new Map<string, [TValue, number, TOnRemoveCallback?]>();

        if (process.env.CURRENT_ENVIRONMENT === 'node') {
            // interval trigger to clear the map -- only on server
            setInterval(() => this._cleanCache(), this.intervalForRemovalInMs);
        }
    }

    /**
     * Gets a value from cache
     *
     * @param {string} key the key
     * @returns {(TValue | undefined)} the value, if it exists
     */
    public get(key: string): TValue | undefined {
        const value = this._cache.get(key);
        if (value) {
            return value[0];
        }
    }

    /**
     * Set a value
     *
     * @param {string} key the key
     * @param {TValue} value the value
     */
    public set(key: string, value: TValue, onRemoveCallback?: TOnRemoveCallback): void {
        const now = Date.now();
        this._cache.set(key, [value, now, onRemoveCallback]);
    }

    public clear(): void {
        this._cache.clear();
    }

    // cleans up the map implementation
    private _cleanCache(): void {
        const now = Date.now();
        const keys = this._cache.keys();
        for (const key of keys) {
            const value = this._cache.get(key);
            if (value && value.length > 1 && now - value[1] > this.removalThresholdInMs) {
                this._cache.delete(key);
                if (typeof value[2] === 'function') {
                    value[2](key);
                }
            }
        }
    }
}
